import { useMemo } from "react";
import {
	formatText,
	FormatterMode,
	getDefaultFormatterOptionsForMode,
	resolveFormatterConfig,
} from "@textformatter/formatter";
import { toast } from "sonner";
import type {
	FormatterMode as FormatterModeValue,
	FormatterModeProfiles,
	FormatterOptions,
	FormatterTransforms,
} from "./types";

export const DEFAULT_TEXT_OPTIONS: FormatterOptions = getDefaultFormatterOptionsForMode(
	FormatterMode.Text,
);
export const DEFAULT_WEB_OPTIONS: FormatterOptions = getDefaultFormatterOptionsForMode(
	FormatterMode.Web,
);
export const DEFAULT_ECOMMERCE_OPTIONS: FormatterOptions = getDefaultFormatterOptionsForMode(
	FormatterMode.Ecommerce,
);

export const DEFAULT_TRANSFORMS: FormatterTransforms = {
	uppercase: false,
	lowercase: false,
	capitalize: false,
	stripHtml: false,
	normalizeWhitespace: false,
};

export function createTransformProfiles(
	overrides?: Partial<Record<FormatterModeValue, Partial<FormatterTransforms>>>,
): Record<FormatterModeValue, FormatterTransforms> {
	return {
		text: { ...DEFAULT_TRANSFORMS, ...(overrides?.text ?? {}) },
		web: { ...DEFAULT_TRANSFORMS, ...(overrides?.web ?? {}) },
		ecommerce: { ...DEFAULT_TRANSFORMS, ...(overrides?.ecommerce ?? {}) },
	};
}

export function createModeProfiles(
	overrides?: Partial<Record<FormatterModeValue, Partial<FormatterOptions>>>,
): FormatterModeProfiles {
	return {
		text: resolveFormatterConfig({
			mode: FormatterMode.Text,
			options: overrides?.text ?? {},
			fallbackMode: FormatterMode.Text,
		}),
		web: resolveFormatterConfig({
			mode: FormatterMode.Web,
			options: overrides?.web ?? {},
			fallbackMode: FormatterMode.Text,
		}),
		ecommerce: resolveFormatterConfig({
			mode: FormatterMode.Ecommerce,
			options: overrides?.ecommerce ?? {},
			fallbackMode: FormatterMode.Text,
		}),
	};
}

export function useFormattedHtml(
	input: string,
	mode: FormatterModeValue,
	options: Partial<FormatterOptions>,
	transforms: Partial<FormatterTransforms>,
) {
	return useMemo(() => {
		if (!input.trim()) return { output: "", format: "html" as const };
		const format = mode === FormatterMode.Text ? ("text" as const) : ("html" as const);
		const output = formatText({
			text: input,
			mode,
			options: resolveFormatterConfig({ options, fallbackMode: FormatterMode.Text }),
			transforms,
			output: format,
		});
		return { output, format };
	}, [input, mode, options, transforms]);
}

export async function copyHtmlToClipboard(html: string) {
	if (!html) return;
	await navigator.clipboard.writeText(html);
	toast.success("HTML copied");
}

export async function copyTextToClipboard(content: string, format: "html" | "text" = "html") {
	if (!content) return;
	const text =
		format === "text"
			? content
			: (() => {
					const temp = document.createElement("div");
					temp.innerHTML = content;
					return temp.innerText;
				})();
	await navigator.clipboard.writeText(text);
	toast.success("Text copied");
}

export async function copyFormattedToClipboard(content: string, format: "html" | "text" = "html") {
	if (!content) return;
	if (format === "text") {
		await navigator.clipboard.writeText(content);
		toast.success("Text copied");
		return;
	}
	if (navigator.clipboard && "ClipboardItem" in window) {
		const temp = document.createElement("div");
		temp.innerHTML = content;
		const text = temp.innerText;
		const item = new ClipboardItem({
			"text/html": new Blob([content], { type: "text/html" }),
			"text/plain": new Blob([text], { type: "text/plain" }),
		});
		await navigator.clipboard.write([item]);
		toast.success("Formatted text copied");
		return;
	}
	await copyTextToClipboard(content, "html");
}
